import { useEffect, useState } from 'react';

/**
 * Client-side pagination for a table. Slices `items` into pages of `pageSize`
 * (default 25) and resets to page 1 whenever the row count changes (e.g. a
 * filter narrows the list). Pair with <Pager/> for the controls.
 *
 * Pass `external` to keep the page number somewhere else — the redesigned tables
 * put it in the URL so a paged view is shareable. When external state is supplied
 * the internal reset is skipped, because the URL hook already drops `page` on any
 * filter change; resetting here as well would fight it.
 */
export function usePaged<T>(
  items: T[],
  pageSize = 25,
  external?: { page: number; onPage: (p: number) => void },
) {
  const [internalPage, setInternalPage] = useState(1);
  const page = external ? external.page : internalPage;
  const setPage = external ? external.onPage : setInternalPage;
  const isExternal = Boolean(external);
  useEffect(() => {
    if (!isExternal) setInternalPage(1);
  }, [items.length, isExternal]);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const cur = Math.min(page, pageCount);
  const start = (cur - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    page: cur,
    pageCount,
    setPage,
    total: items.length,
    start,
    pageSize,
  };
}

export function Pager({
  page,
  pageCount,
  total,
  start,
  pageSize,
  onPage,
}: {
  page: number;
  pageCount: number;
  total: number;
  start: number;
  pageSize: number;
  onPage: (p: number) => void;
}) {
  if (total <= pageSize) return null; // nothing to page through
  const from = start + 1;
  const to = Math.min(start + pageSize, total);
  return (
    <div className="pager">
      <span className="muted">
        Showing {from}–{to} of {total}
      </span>
      <div className="pager-btns">
        <button className="mini-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ‹ Prev
        </button>
        <span className="muted">
          Page {page} / {pageCount}
        </span>
        <button className="mini-btn" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
          Next ›
        </button>
      </div>
    </div>
  );
}
